import { useState, useEffect } from "react";

// Import necessary components for drag-and-drop
import { DndContext, closestCorners } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

// Import utility functions for generating unique IDs
import { v4 as uuidv4 } from "uuid";

// Import necessary components and styles
import { Empty, Skeleton, Button, Input } from "antd";

import EBulletinCarousel from "../../components/EBulletin/EBulletinCarousel";
import AddSectionCard from "../../components/EBulletin/EbulletinAddSection";
import SectionCard from "../../components/EBulletin/cards/SectionCard";
import { useBulletinSectionService, useBulletinService } from "../../services";

import { useSelector } from "react-redux";

import "../../styles/bulletinAdmin/components/bulletinContent.scss";

export default function EBulletinContent({ isAdmin, onBulletinUpdate }) {
  const [sections, setSections] = useState([]);
  const [currentBulletin, setCurrentBulletin] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { ebullCurrentTab } = useSelector((state) => state.generalSlice);

  const {
    service: sectionService,
    response: sectionResponse,
    loading: sectionLoading,
  } = useBulletinSectionService();

  const { service: bulletinService } = useBulletinService();

  // Update current bulletin when the bulletin prop changes
  // useEffect(() => {
  //   setCurrentBulletin(bulletin);
  // }, [bulletin]);

  // // Fetch sections when bulletinId changes
  // useEffect(() => {
  //   const fetchSections = async () => {
  //     if (!bulletinId) return;
  //     try {
  //       await sectionService.getAllSections(bulletinId);
  //     } catch (err) {
  //       console.error("Failed to fetch sections:", err);
  //     }
  //   };
  //   fetchSections();
  // }, [bulletinId]);

  // Fetch the bulletin meta/details whenever activeId changes
  useEffect(() => {
    console.log("1");

    let mounted = true;
    const run = async () => {
      if (!ebullCurrentTab) {
        setCurrentBulletin(null);
        setSections([]);
        return;
      }
      try {
        const res = await bulletinService.getBulletin(ebullCurrentTab);
        const data = res?.data ?? res ?? null;
        if (mounted) setCurrentBulletin(data || null);
      } catch (e) {
        console.error("Failed to load bulletin:", e);
        if (mounted) setCurrentBulletin(null);
      }
    };
    run();
    return () => (mounted = false);
  }, [ebullCurrentTab]);

  // Fetch sections whenever activeId changes
  useEffect(() => {
    const fetchSections = async () => {
      if (!ebullCurrentTab) return;
      try {
        await sectionService.getAllSections(ebullCurrentTab);
      } catch (err) {
        console.error("Failed to fetch sections:", err);
      }
    };
    fetchSections();
  }, [ebullCurrentTab]);

  console.log(sectionResponse);

  // Update sections when response changes
  // useEffect(() => {
  //   if (response?.data) {
  //     const rawData = Array.isArray(response.data) ? response.data : [];
  //     const normalized = rawData.map((section) => ({
  //       ...section,
  //       title: section.section_title || "",
  //       blocks: Array.isArray(section.content)
  //         ? section.content.map((block) => ({
  //             id: block.id,
  //             type: block.type,
  //             content: block.content,
  //             display_order: block.display_order,
  //             is_active: block.is_active,
  //             contentId: block.id,
  //           }))
  //         : [],
  //     }));
  //     setSections(normalized);
  //   } else if (!loading) {
  //     setSections([]);
  //   }
  // }, [response, loading]);

  useEffect(() => {
    if (Array.isArray(sectionResponse?.data)) {
      const normalized = sectionResponse.data.map((section) => ({
        ...section,
        title: section.section_title || "",
        blocks: Array.isArray(section.content)
          ? section.content.map((block) => ({
              id: block.id,
              type: block.type,
              content: block.content,
              display_order: block.display_order,
              is_active: block.is_active,
              contentId: block.id,
            }))
          : [],
      }));
      setSections(normalized);
    } else if (!sectionLoading) {
      setSections([]);
    }
  }, [sectionResponse, sectionLoading]);

  // Function to add a new section
  const onAddSection = () => {
    if (!isAdmin) return;
    const tempId = uuidv4();
    const blockId = uuidv4();

    setSections((prev) => [
      ...prev,
      {
        tempId,
        id: null,
        title: "",
        display_order: prev.length,
        blocks: [
          {
            id: blockId,
            type: "text",
            content: "",
          },
        ],
        isEditing: true,
      },
    ]);
  };

  const reloadSections = async () => {
    if (!ebullCurrentTab) return;
    try {
      await sectionService.getAllSections(ebullCurrentTab);
    } catch (err) {
      console.error("Failed to reload sections:", err);
    }
  };

  const reloadBulletin = async () => {
    if (!ebullCurrentTab) return;
    try {
      const response = await bulletinService.getBulletin(ebullCurrentTab);
      const updatedBulletin = response?.data ?? response ?? null;
      if (updatedBulletin) {
        setCurrentBulletin(updatedBulletin);
        onBulletinUpdate?.(updatedBulletin);
      }
    } catch (err) {
      console.error("Failed to reload bulletin:", err);
    }
  };

  // Handle drag-and-drop reordering of sections
  const handleDragEnd = async (event) => {
    if (!isAdmin) return;
    const { active, over } = event;
    if (!active || !over || active.id === over.id) return;

    const oldIndex = sections.findIndex(
      (s) => (s.id || s.tempId) === active.id
    );
    const newIndex = sections.findIndex((s) => (s.id || s.tempId) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove([...sections], oldIndex, newIndex);

    try {
      await Promise.all(
        reordered.map((sec, idx) => {
          if (sec.id && typeof sec.id === "number") {
            return sectionService.updateSection(ebullCurrentTab, sec.id, {
              display_order: idx,
            });
          }
          return null;
        })
      );
      await reloadSections();
    } catch (err) {
      console.error("Failed to persist reorder:", err);
    }
  };

  // Filter sections based on search query and admin status
  const displayableSections = sections
    .filter((section) =>
      section.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .filter((section) => (isAdmin ? true : !section.isEditing));

  return (
    <div>
      <EBulletinCarousel
        bulletin={currentBulletin}
        isAdmin={isAdmin}
        reloadBulletin={reloadBulletin}
      />


      <div>
        {sectionLoading ? (
          <div>
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        ) : (
          <>
            <div className="page-header">
              <Input.Search
                placeholder="Search sections by title..."
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: 300 }}
                allowClear
              />
              {isAdmin && (
                <Button
                  onClick={() => setIsReordering(!isReordering)}
                  type="primary"
                >
                  {isReordering ? "Done Reordering" : "Reorder Sections"}
                </Button>
              )}
            </div>

            {displayableSections.length === 0 ? (
              <div style={{ padding: "24px", textAlign: "center" }}>
                <Empty
                  description={
                    searchQuery
                      ? "No sections match your search."
                      : "This bulletin has no content yet."
                  }
                />
              </div>
            ) : (
              <DndContext
                collisionDetection={closestCorners}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={displayableSections.map((s) => s.id || s.tempId)}
                  strategy={verticalListSortingStrategy}
                >
                  {displayableSections.map((section, index) => (
                    <SectionCard
                      key={section.id || section.tempId}
                      id={section.id || section.tempId}
                      section={section}
                      sectionIdx={index}
                      sections={sections}
                      setSections={setSections}
                      bulletinId={ebullCurrentTab}
                      reloadSections={reloadSections}
                      isAdmin={isAdmin}
                      isReordering={isReordering}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </>
        )}

        {isAdmin && !sectionLoading && !isReordering && (
          <div style={{ paddingBottom: "24px" }}>
            <AddSectionCard onClick={onAddSection} />
          </div>
        )}
      </div>
    </div>
  );
}
